import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux';

import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';

import Paper from '@mui/material/Paper';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

import clsx from 'clsx';
import useStyles from '../styles/Styles';

import { makeRequest, resetErrorMessage, resetLoginFailure } from '../actions';

import _ from 'lodash';
import * as yup from 'yup';
import { isTrue } from '../utils';

const LoginAs = () => {
	const dispatch = useDispatch();
	const generalSettings = useSelector(state => state.settings.general);
	const loginError = useSelector(state => state.requests.login_error);
	
	const canLogin = (generalSettings.data.umas_status === 2 || (generalSettings.data.umas_status === 1 && isTrue(generalSettings.data.early_enrol_on) && isTrue(generalSettings.data.enrolling_on)));
	
	const classes = useStyles();
	
	const schema = yup.object().shape({
		user: yup.string().matches(/^[0-9]+$/, { message: 'Please enter your own member number'}),
		password: yup.string().required('Please enter your password'),
		member_no: yup.string().matches(/^[0-9]+$/, { message: 'Please enter the member number of the member on behalf of whom you would like to log in'}),
	});
	
	const [data, setData] = useState({
		user: '',
		password: '',
		member_no: ''
	})
	
	const [validator, setValidator] = useState({
		user: {
			error: false,
			message: ''
		},
		password: {
			error: false,
			message: ''
		},
		member_no : {
			error: false,
			message: ''
		}
	});
	
	const [showPassword, setShowPassword] = useState(false);
	
	const fields = [
		{
			id: 'user',
			atts: {
				label: "Your member number",
				value: data.user,
			}
		},
		{
			id: 'password',
			atts: {
				label: "Your password",
				type: showPassword ? "text" : "password",
				value: data.password,
				InputProps: {
					endAdornment: (
					  <InputAdornment position="end">
						<IconButton
						  aria-label="toggle password visibility"
						  onClick={() => setShowPassword(!showPassword)}
						  onMouseDown={(event) => event.preventDefault()}
						  edge="end"
						>
						{showPassword ? <Visibility /> : <VisibilityOff />}
						</IconButton>
					  </InputAdornment>
					)
				}
			}
		},
		{
			id: 'member_no',
			atts: {
				label: "The member's member no",
				value: data.member_no,
			}
		},
	];
	
	const storeAndValidate = (key, value) => {
		setData({...data, [key]: value})

		schema.validateAt(key, {[key]: value})
		.then(val => {
			setValidator({...validator, [key]: {error: false, message: ''}})
		})
		.catch(err => {
			console.log(err);
			setValidator({...validator, [key]: {error: true, message: err.message}})
		})
	}
	
	const [submitting, setSubmitting] = useState(false);
	
	useEffect(() => {
		if(loginError){
			setSubmitting(false);
		}
	}, [loginError])
	
	const maybeSubmit = (event) => {
		event.preventDefault();
		
		dispatch(resetErrorMessage());
		dispatch(resetLoginFailure());
		
		schema.validate(data, {abortEarly: false})
		.then(value => {
			setSubmitting(true);
			dispatch(makeRequest('login_as', {
				user: data.user,
				password: data.password, 
				login_as: data.member_no
			}));
		}).catch(err => {
			const v = _.cloneDeep(validator);
			
			_.each(err.inner, (e) => {
				v[e.path].error = true;
				v[e.path].message = e.message;
			})
			
			setValidator(v);
		})
	}
	
	
	return (
		<>
		<ErrorDisplay />
		
		<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
		<PageTitle title='Log in as' />
		
		{canLogin ?
		( submitting ? 
		<Waiting message='Logging in...' /> :
		<>
			<Typography variant="body2" paragraph>
			If you have an admin-level user account, you can log in to U-MAS for Members on behalf of another member.
			</Typography>
			<form className={classes.form} noValidate> 
			<Grid container spacing={2}>
			{fields.map((f, i) => (
				<Grid item xs={12} key={i} >
				<TextField 
					variant="outlined"
					required
					fullWidth
					error={validator[f.id].error}
					helperText={validator[f.id].message}
					onChange={(event) => storeAndValidate(f.id, event.target.value )}
					{...f.atts}
					/>
				</Grid>
			))}
				<Grid item xs={12}>
				<Button
					type="submit"
					fullWidth
					variant="contained"
					color="primary"
					className={classes.submit}
					onClick={(event) => maybeSubmit(event)}
				  >Submit
				  </Button>
				</Grid>
			</Grid>
			</form>
		</> ) : 
		( generalSettings.expires === 0 ?
		<Waiting message='Loading. Please wait..' /> :
		<>
		<Typography variant="h6">
		Login is off
		</Typography>
		<Typography variant="body2">
		Sorry, logging in is currently disabled. Please check again later, or <Link to="/contact">contact us</Link> for more information.
		</Typography>
		</>
		)}
		</Paper>
		</>
	)};

export default LoginAs